/**
 * Entity Mention Model and Extraction Utility
 *
 * Handles the @mention autocomplete system data model for referencing
 * entities (APIs, collections, projects, folders, and control-flow nodes)
 * in orchestration AI prompts.
 *
 * Trigger characters:
 *   @ → APIs (existing)
 *   / → Projects, Collections, Folders (hierarchical drill-down)
 *   # → Control-flow nodes (IF_ELSE, LOOP, DELAY, ERROR_CATCH)
 *
 * Requirements: 1.5, 12.3, 12.5, 7.1, 7.2 (ai-prompt-smart-mentions)
 */

/**
 * @typedef {Object} EntityMention
 * @property {number} entityId - Database ID of the entity (negative for synthetic node types)
 * @property {string} entityType - One of "api" | "collection" | "project" | "folder" | "node"
 * @property {string} entityName - Display name of the entity
 */

/**
 * @typedef {Object} MentionToken
 * @property {string} displayText - Text shown in the pill (e.g., "Login API")
 * @property {EntityMention} mention - Structured mention metadata
 * @property {number} startIndex - Start position of the token in the raw prompt text
 * @property {number} endIndex - End position of the token in the raw prompt text
 */

/**
 * Valid entity types for the mention autocomplete system.
 */
export const ENTITY_TYPES = Object.freeze({
  API: 'api',
  COLLECTION: 'collection',
  PROJECT: 'project',
  FOLDER: 'folder',
  NODE: 'node',
});

/**
 * Trigger characters that activate the mention dropdown for each entity category.
 */
export const TRIGGER_CHARACTERS = Object.freeze({
  API: '@',
  NODE: '#',
});

/**
 * Synthetic negative IDs for control-flow node types.
 * These don't correspond to any database entity — they are instruction tokens
 * that tell the AI where to place control-flow nodes in the generated flow.
 */
export const NODE_TYPE_IDS = Object.freeze({
  IF_ELSE: -100,
  LOOP: -101,
  DELAY: -102,
  ERROR_CATCH: -103,
});

/**
 * Human-readable labels for each node type.
 */
export const NODE_TYPE_LABELS = Object.freeze({
  IF_ELSE: 'If/Else Condition',
  LOOP: 'Loop',
  DELAY: 'Delay',
  ERROR_CATCH: 'Error Catch',
});

/**
 * Menu items for the # trigger dropdown — each represents a control-flow node
 * the user can insert into their prompt.
 */
export const NODE_TYPES_MENU = Object.freeze([
  {
    id: NODE_TYPE_IDS.IF_ELSE,
    type: ENTITY_TYPES.NODE,
    name: 'IF_ELSE',
    label: NODE_TYPE_LABELS.IF_ELSE,
    description: 'Branch flow based on a condition',
  },
  {
    id: NODE_TYPE_IDS.LOOP,
    type: ENTITY_TYPES.NODE,
    name: 'LOOP',
    label: NODE_TYPE_LABELS.LOOP,
    description: 'Repeat steps multiple times',
  },
  {
    id: NODE_TYPE_IDS.DELAY,
    type: ENTITY_TYPES.NODE,
    name: 'DELAY',
    label: NODE_TYPE_LABELS.DELAY,
    description: 'Wait before continuing',
  },
  {
    id: NODE_TYPE_IDS.ERROR_CATCH,
    type: ENTITY_TYPES.NODE,
    name: 'ERROR_CATCH',
    label: NODE_TYPE_LABELS.ERROR_CATCH,
    description: 'Handle errors from a step',
  },
]);

/**
 * Creates a structured EntityMention object.
 *
 * @param {number} entityId - Database ID of the entity
 * @param {string} entityType - One of "api" | "collection" | "project" | "folder"
 * @param {string} entityName - Display name of the entity
 * @returns {EntityMention}
 */
export function createEntityMention(entityId, entityType, entityName) {
  return {
    entityId,
    entityType,
    entityName,
  };
}

/**
 * Creates a MentionToken object representing a pill inserted into the prompt textarea.
 *
 * @param {string} displayText - Text shown in the pill
 * @param {EntityMention} mention - Structured mention metadata
 * @param {number} startIndex - Start position in the prompt text
 * @param {number} endIndex - End position in the prompt text
 * @returns {MentionToken}
 */
export function createMentionToken(displayText, mention, startIndex, endIndex) {
  return {
    displayText,
    mention,
    startIndex,
    endIndex,
  };
}

/**
 * Extract all mention tokens from the prompt textarea's rich content.
 *
 * When called without the raw text, this function builds the prompt text
 * from the tokens' display names (sorted by position) and extracts the
 * structured EntityMention array for sending to the AI service.
 *
 * For full raw text replacement, use `extractMentionsFromRawText` instead.
 *
 * @param {Array<MentionToken>} mentionTokens - Tracked mention insertions from the textarea
 * @returns {{promptText: string, mentions: EntityMention[]}}
 */
export function extractMentions(mentionTokens) {
  if (!mentionTokens || mentionTokens.length === 0) {
    return { promptText: '', mentions: [] };
  }

  // Extract unique mentions (deduplicate by entityId + entityType)
  const mentionsMap = new Map();
  for (const token of mentionTokens) {
    const key = `${token.mention.entityId}-${token.mention.entityType}`;
    if (!mentionsMap.has(key)) {
      mentionsMap.set(key, {
        entityId: token.mention.entityId,
        entityType: token.mention.entityType,
        entityName: token.mention.entityName,
      });
    }
  }

  const mentions = Array.from(mentionsMap.values());

  // Build prompt text from tokens sorted by position, joining display names
  const ascTokens = [...mentionTokens].sort(
    (a, b) => a.startIndex - b.startIndex
  );
  const promptText = ascTokens.map((token) => token.displayText).join(' ');

  return { promptText, mentions };
}

/**
 * Extracts mentions from a raw text string by replacing pill token ranges
 * with plain text entity names.
 *
 * This is the full version that takes the raw prompt text and replaces
 * pill placeholder regions (identified by mentionTokens) with the display names.
 *
 * @param {string} rawText - The raw text from the prompt textarea (with pill placeholders)
 * @param {Array<MentionToken>} mentionTokens - Tracked mention insertions
 * @returns {{promptText: string, mentions: EntityMention[]}}
 */
export function extractMentionsFromRawText(rawText, mentionTokens) {
  if (!mentionTokens || mentionTokens.length === 0) {
    return { promptText: rawText || '', mentions: [] };
  }

  // Sort tokens by startIndex descending for safe in-place replacement
  const sortedTokens = [...mentionTokens].sort(
    (a, b) => b.startIndex - a.startIndex
  );

  // Replace pill placeholders with plain text entity names (from end to start)
  let promptText = rawText;
  for (const token of sortedTokens) {
    const before = promptText.slice(0, token.startIndex);
    const after = promptText.slice(token.endIndex);
    promptText = before + token.displayText + after;
  }

  // Extract unique mentions
  const mentionsMap = new Map();
  for (const token of mentionTokens) {
    const key = `${token.mention.entityId}-${token.mention.entityType}`;
    if (!mentionsMap.has(key)) {
      mentionsMap.set(key, {
        entityId: token.mention.entityId,
        entityType: token.mention.entityType,
        entityName: token.mention.entityName,
      });
    }
  }

  const mentions = Array.from(mentionsMap.values());

  return { promptText, mentions };
}
